'use client'

import { AspectRatioValues } from '@/components/image-cropper/types'
import { useAuth } from '@/contexts/AuthContext'
import { storage } from '@/lib/firebase'
import { updateUser } from '@/services/users'
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage'
import { CroppableImageUploader } from './CroppableImageUploader'

interface ProfileImageUploaderProps {
  userId: string
  currentImageUrl: string | undefined
  onImageUpdated: (url: string) => void
  canEdit?: boolean
}

export function ProfileImageUploader({
  userId,
  currentImageUrl,
  onImageUpdated,
  canEdit = false,
}: ProfileImageUploaderProps) {
  const { setUserData } = useAuth()

  const uploadProfilePicture = async (file: File, onProgress?: (progress: number) => void): Promise<string> => {
    return new Promise((resolve, reject) => {
      try {
        const storageRef = ref(storage, `avatars/${userId}/${file.name}`)

        const uploadTask = uploadBytesResumable(storageRef, file)

        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100

            onProgress?.(progress)
          },
          (error) => {
            console.error('Upload failed:', error)
            reject(error)
          },
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)

            await updateUser(userId, { profilePicUrl: downloadURL })

            // Update AuthContext with new profile picture URL
            setUserData((prev) => (prev ? { ...prev, profilePicUrl: downloadURL } : prev))

            resolve(downloadURL)
          },
        )
      } catch (error) {
        console.error('Error starting upload:', error)
        reject(error)
      }
    })
  }

  const deleteProfilePicture = async () => {
    if (!currentImageUrl) return

    const imageRef = ref(storage, currentImageUrl)
    await deleteObject(imageRef)

    await updateUser(userId, { profilePicUrl: '' })

    // Update AuthContext with empty profile picture URL
    setUserData((prev) => (prev ? { ...prev, profilePicUrl: '' } : prev))
  }

  return (
    <CroppableImageUploader
      userId={userId}
      currentImageUrl={currentImageUrl}
      onImageUpdated={onImageUpdated}
      onUpload={uploadProfilePicture}
      onDelete={deleteProfilePicture}
      canEdit={canEdit}
      imageAlt='Profile picture'
      successMessage='Profile picture updated successfully'
      deleteSuccessMessage='Profile picture removed successfully'
      errorMessage='Failed to update profile picture'
      deleteErrorMessage='Failed to remove profile picture'
      aspectRatio={AspectRatioValues.ONE_TO_ONE}
    />
  )
}
